(() => {
  if (customElements.get('doouyou-newsletter-popup')) return;
  const stateKey = 'doouyou:newsletter-popup:v1';
  const pendingKey = 'doouyou:newsletter-popup:pending';
  const day = 86400000;
  let memoryState = null;
  function availableStorage(name) {
    try {
      const storage = window[name];
      const probe = `${stateKey}:probe`;
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return storage;
    } catch (_) { return null; }
  }
  const storage = availableStorage('localStorage') || availableStorage('sessionStorage');
  function readState() {
    try { return JSON.parse(storage?.getItem(stateKey) || 'null') || memoryState; }
    catch (_) { return memoryState; }
  }
  function saveState(days, subscribed = false) {
    memoryState = { until: Date.now() + days * day, subscribed };
    try { storage?.setItem(stateKey, JSON.stringify(memoryState)); } catch (_) {}
  }
  function writePending(value) {
    try {
      if (value) sessionStorage.setItem(pendingKey, String(Date.now()));
      else sessionStorage.removeItem(pendingKey);
    } catch (_) {}
  }
  function wasPending() {
    try {
      const timestamp = Number(sessionStorage.getItem(pendingKey));
      return timestamp > 0 && Date.now() - timestamp < 30 * 60000;
    } catch (_) { return false; }
  }
  customElements.define('doouyou-newsletter-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.isEditor = this.dataset.designMode === 'true';
      this.wasShown = false;
      this.autoEligible = false;
      this.readyAt = performance.now() + 8000;
      this.lastActivity = performance.now();
      const act = () => {
        this.lastActivity = performance.now();
        this.scheduleAutoOpen();
      };
      document.addEventListener('keydown', act, { passive: true, signal });
      document.addEventListener('pointerdown', act, { passive: true, signal });
      window.addEventListener('scroll', act, { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.lastActivity = performance.now();
        this.scheduleAutoOpen();
      }, { signal });
      for (const event of ['focusout', 'shopify-chat:close']) {
        document.addEventListener(event, () => this.scheduleAutoOpen(), { signal });
      }
      // A native close event does not bubble; capture it after any other modal is dismissed.
      document.addEventListener('close', () => this.scheduleAutoOpen(), { capture: true, signal });
      window.addEventListener('storage', event => {
        if (!event.key || event.key === stateKey) this.scheduleAutoOpen();
      }, { signal });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-popup-close]')) this.dismiss();
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dismiss();
        }
      }, { signal });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.dismiss();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('kn-popup-open');
        if (this.returnFocus?.isConnected && this.returnFocus !== document.body) this.returnFocus.focus({ preventScroll: true });
      }, { signal });
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-doouyou-popup-open]');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (this.isEditor && event.detail?.sectionId === this.dataset.sectionId) this.open(event.target);
      }, { signal });
      this.form?.addEventListener('submit', event => {
        if (!this.form.reportValidity()) { event.preventDefault(); return; }
        if (this.form.dataset.submitting === 'true') { event.preventDefault(); return; }
        this.form.dataset.submitting = 'true';
        writePending(true);
        const submit = this.form.querySelector('[type="submit"]');
        if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
      }, { signal });
      window.addEventListener('pageshow', () => {
        this.resetSubmit();
        this.scheduleAutoOpen();
      }, { signal });
      const url = new URL(window.location.href);
      const responseMarker = url.searchParams.get('doouyou_newsletter') === '1' || url.hash === `#${this.dataset.formId}` || wasPending();
      const success = this.querySelector('[data-popup-success]');
      const error = this.querySelector('[data-popup-error]');
      const subscribedResponse = this.dataset.subscribed === 'true' && wasPending();
      if (responseMarker && (success || error || subscribedResponse)) {
        if (success || subscribedResponse) saveState(90, true);
        writePending(false);
        url.searchParams.delete('doouyou_newsletter');
        if (url.hash === `#${this.dataset.formId}`) url.hash = '';
        if (success || subscribedResponse) url.searchParams.delete('customer_posted');
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
        this.open(null);
      } else if (!this.isEditor && this.dataset.home === 'true' && this.dataset.subscribed !== 'true' && !(readState()?.until > Date.now())) {
        this.autoEligible = true;
        this.scheduleAutoOpen();
      }
      this.cleanup = true;
    }
    resetSubmit() {
      if (!this.form) return;
      delete this.form.dataset.submitting;
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
    }
    blocked() {
      if (document.visibilityState !== 'visible') return true;
      if (document.querySelector('shopify-chat[open]')) return true;
      if (document.body.classList.contains('ks-support-open') || document.documentElement.classList.contains('dy-site-drawer-open')) return true;
      const active = document.activeElement;
      if (active?.matches('input, textarea, select, [contenteditable="true"]')) return true;
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(element => {
        if (element === this.dialog) return false;
        if (element.tagName === 'DIALOG' && element.getAttribute('aria-modal') !== 'true') {
          let modal = false;
          try { modal = element.matches(':modal'); } catch (_) {}
          if (!modal) return false;
        }
        if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
        const style = getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
      });
    }
    prepareArtwork() {
      const image = this.querySelector('img[data-src]');
      if (image && !image.src) image.src = image.dataset.src;
    }
    scheduleAutoOpen() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      if (!this.autoEligible || this.wasShown || readState()?.until > Date.now() || document.visibilityState !== 'visible') return;
      // Warm only eligible homepages, before the display delay, without loading a popup SDK.
      this.prepareArtwork();
      const delay = Math.max(0, this.readyAt - performance.now(), this.lastActivity + 1500 - performance.now());
      this.timer = window.setTimeout(() => {
        this.timer = 0;
        this.tryAutoOpen();
      }, delay);
    }
    tryAutoOpen() {
      if (!this.autoEligible || this.wasShown || readState()?.until > Date.now()) return;
      if (performance.now() < this.readyAt || performance.now() - this.lastActivity < 1500) {
        this.scheduleAutoOpen();
        return;
      }
      // A blocker releases this one-shot schedule through an interaction, close or visibility event.
      if (this.blocked()) return;
      this.open(null);
    }
    open(trigger) {
      if (this.dialog.open || this.blocked()) return;
      this.returnFocus = trigger || document.activeElement;
      this.prepareArtwork();
      this.dialog.showModal();
      document.body.classList.add('kn-popup-open');
      this.wasShown = true;
      window.clearTimeout(this.timer);
      this.timer = 0;
    }
    dismiss() {
      if (!this.dialog.open) return;
      const saved = readState();
      if (!this.querySelector('[data-popup-success]') && !(saved?.subscribed && saved.until > Date.now())) saveState(7);
      this.dialog.close();
    }
    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.autoEligible = false;
      this.abort?.abort();
      if (this.dialog?.open) { this.dialog.close(); document.body.classList.remove('kn-popup-open'); }
      this.cleanup = false;
    }
  });
})();
